import { calculateRowAmounts } from '../../item-details/item-utils';

export interface TargetCustomerSnapshot {
    name?: string;
    addressId?: string;
    address?: string;
    addressCountryCode?: string;
    isCustom?: number;
    customAddress?: string;
    phoneNumber?: string;
    mail?: string;
    customerAddress?: string;
    vatRegistrationNo?: string;
    txap?: string | number;
    pltb?: string;
    defaultPriceList?: string;
    cucd?: string;
    cfc4?: string;
    outstanding?: number | string;
    totalOverdue?: number | string;
    okcrl3?: string | number;
    odud?: string | number;
    nood?: string | number;
    F1N096?: string | number;
    blcd?: string | null;
    creditLimit3?: number | string;
}

export interface CopyDiffCustomerNavState {
    copyFromOrderNumber: string;
    copyMode: string;
    targetCustomerId: string;
    targetCustomerSnapshot: TargetCustomerSnapshot;
}

/** Normalize currency codes for comparison (trim + upper). Empty matches empty. */
export function normalizeCurrencyCode(value: unknown): string {
    return String(value ?? '')
        .trim()
        .toUpperCase();
}

/**
 * Cross-currency copy is unsafe when rates are kept (product decision 1C).
 * Returns true when both sides have a currency and they differ.
 */
export function isCopyCurrencyMismatch(sourceCucd: unknown, targetCucd: unknown): boolean {
    const source = normalizeCurrencyCode(sourceCucd);
    const target = normalizeCurrencyCode(targetCucd);
    if (!source || !target) {
        return false;
    }
    return source !== target;
}

export function shouldFetchVatForCustomer(params: {
    txap: unknown;
    customerCountryCode: unknown;
    warehouseCountryCode: unknown;
}): boolean {
    const txap = Number(params.txap);
    if (txap === 0 || Number.isNaN(txap)) {
        return false;
    }
    if (txap !== 1) {
        return false;
    }
    const customerCountry = String(params.customerCountryCode ?? '')
        .trim()
        .toUpperCase();
    const warehouseCountry = String(params.warehouseCountryCode ?? '')
        .trim()
        .toUpperCase();
    return !!(customerCountry && warehouseCountry && customerCountry === warehouseCountry);
}

/** Apply copy-mode qty/rate clearing on top of a mapped source line (1C keeps rate unless mode clears it). */
export function applyCopyModeToLine(line: any, copyMode: string): any {
    const mode = copyMode || 'ALL';
    if (mode !== 'HEADER_ITEMS_NO_QTY' && mode !== 'HEADER_ITEMS_NO_QTY_NO_RATE') {
        return { ...line };
    }
    const base: any = {
        ...line,
        quantity: 0,
        amount: 0,
        discountAmount: 0,
        amountAfterDiscount: 0,
        vatAmount: 0,
        amountAfterVat: 0,
        copyOrderStockError: undefined,
        avlStock: undefined
    };
    if (mode === 'HEADER_ITEMS_NO_QTY_NO_RATE') {
        base.rate = 0;
        base.discount = 0;
    }
    return base;
}

/**
 * Apply VAT policy to a line and recalculate amounts.
 * When skipVat: force vat=0. When apply: use vatPercent (and optional vatCode).
 */
export function applyVatPolicyAndRecalcAmounts(
    line: any,
    opts: { shouldFetchVat: boolean; vatPercent?: number | null; vatCode?: string | null }
): any {
    const next = { ...line };
    if (!opts.shouldFetchVat) {
        next.vat = 0;
    } else if (opts.vatPercent !== undefined && opts.vatPercent !== null && !Number.isNaN(Number(opts.vatPercent))) {
        next.vat = Number(opts.vatPercent);
    }
    if (opts.vatCode !== undefined && opts.vatCode !== null && String(opts.vatCode).trim() !== '') {
        next.vatCode = opts.vatCode;
    }
    return calculateRowAmounts(next);
}

/** Clear source order MCM so getPriceInfo applies target F1N096. */
export function clearStoredOrderMcmFields(target: any): void {
    if (!target) {
        return;
    }
    target.orderMcm = undefined;
    target.minimumOrderMargin = undefined;
    target.F1N096 = undefined;
    target.orderMarginBelowMinimum = undefined;
}

/** Merge target customer identity/address/credit onto a copied header object. Keeps FACI/WH/OT from source. */
export function rebindHeaderToTargetCustomer(
    sourceHeader: any,
    targetCustomerId: string,
    snapshot: TargetCustomerSnapshot
): any {
    const merged = { ...sourceHeader };
    const name = snapshot.name || merged.customerName || merged.name || '';
    merged.customerId = targetCustomerId;
    merged.customer = targetCustomerId;
    merged.CUNO = targetCustomerId;
    merged.customerName = name;
    merged.CUNM = name;
    merged.name = name;

    merged.addressId = snapshot.addressId ?? '';
    merged.address = snapshot.address ?? '';
    merged.addressCountryCode = snapshot.addressCountryCode ?? '';
    merged.countryCode = snapshot.addressCountryCode ?? merged.countryCode;
    merged.isCustom = snapshot.isCustom === 1 ? 1 : 0;
    merged.customAddress = snapshot.isCustom === 1 ? snapshot.customAddress || '' : '';
    merged.phoneNumber = snapshot.phoneNumber ?? '';
    merged.mail = snapshot.mail ?? '';
    merged.customerAddress = snapshot.customerAddress ?? '';
    merged.vatRegistrationNo = snapshot.vatRegistrationNo ?? '';

    merged.txap = snapshot.txap;
    merged.pltb = snapshot.pltb ?? '';
    merged.defaultPriceList = snapshot.defaultPriceList ?? '';
    merged.cucd = snapshot.cucd ?? '';
    merged.cfc4 = snapshot.cfc4 ?? '';

    merged.outstanding = snapshot.outstanding ?? 0;
    merged.totalOverdue = snapshot.totalOverdue ?? 0;
    merged.okcrl3 = snapshot.okcrl3;
    merged.odud = snapshot.odud;
    merged.nood = snapshot.nood;
    merged.creditLimit3 = snapshot.creditLimit3 ?? snapshot.okcrl3;
    merged.blcd = snapshot.blcd;

    // Clear source MCM so subsequent getPriceInfo applies target F1N096 from Details.
    clearStoredOrderMcmFields(merged);

    merged.orderNumber = 'New Order';
    merged.orderId = undefined;
    merged.referenceOrderId = '';

    return merged;
}

export function resolveNumericCustomerId(value: unknown): string {
    const v = String(value ?? '').trim();
    return /^\d+$/.test(v) ? v : '';
}
